"use client";
import React, { useState, useRef, useEffect } from "react";
import CDNImage from "@/components/cdnImage/cdnImage";
import { CategoryProps, CategoryItem } from "./type";
import VectorLeft from "@/assets/icons/VectorLeft.svg";
import VectorRight from "@/assets/icons/VectorRight.svg";
import SafeLink from "@/components/SafeLink";
import { buildSearchUrl } from "../../util";
import styles from "./thirdLevelCate.module.scss";
import _ from "lodash";

const ThirdLevelCate: React.FC<CategoryProps> = ({ data, searchParams, basePath }) => {
  const { l1: _l1, l2: _l2, l3: _l3 } = searchParams;
  const [l1, l2, l3] = [~~_l1, ~~_l2, ~~_l3];
  // const currentCategory = data?.find((item: CategoryItem) => item.id === l1)?.childrenList?.find((item) => item.id === l2);
  const currentCategory = data?.find((item: CategoryItem) => item.id === l1)?.childrenList;
  let categoryList = _.flatten(currentCategory?.map((v) => v.childrenList)) || []; // 三级类目列表
  const [l3Clicked, setL3Clicked] = useState(l3);

  const [startIndex, setStartIndex] = useState<number>(0); // 起点下标
  const categoryItemWidth = 92;

  const categoryWinRef = useRef<HTMLDivElement>(null); // 类目窗口的ref
  const maxVisibleCategories = 10; // 最多展示类目数量
  const maxScrollCategories = 8; // 一次性最多滚动的类目数量

  useEffect(() => {
    if (l3) {
      const l2CateIndex = categoryList?.findIndex((item: CategoryItem) => item.id === l3) || 0;
      initScrollLeft(l2CateIndex);
    }
  }, []);

  const initScrollLeft = (index: number) => {
    const currentWinIndex = index - startIndex + 1; // 当前选中类目在窗口的索引
    // 点击类目当前下标大于5时，需要滚动到窗口中间);
    if (currentWinIndex > 5 && categoryWinRef.current) {
      categoryWinRef.current.scrollTo({
        left: (startIndex + currentWinIndex - 5.5) * (categoryItemWidth + 16),
        behavior: "smooth",
      });
    }
  };

  // 点击类目向左滚动
  const handlePrevClick = () => {
    const categoryIndex = Math.max(startIndex - maxScrollCategories, 0);
    setStartIndex(categoryIndex);
    if (categoryWinRef.current) {
      categoryWinRef.current.scrollTo({
        left: categoryIndex * (categoryItemWidth + 22),
        behavior: "smooth",
      });
    }
  };

  // 点击类目向右滚动
  const handleNextClick = () => {
    // 最多滚动8个不能超出类目数量
    const categoryIndex = Math.min(startIndex + maxScrollCategories, categoryList.length - maxVisibleCategories);
    setStartIndex(categoryIndex);
    if (categoryWinRef.current) {
      categoryWinRef.current.scrollTo({
        left: categoryIndex * (categoryItemWidth + 22),
        behavior: "smooth",
      });
    }
  };

  // 选择二级类目
  const handleSelectThirdCategory = (category: CategoryItem, index: number) => {
    const currentWinIndex = index - startIndex + 1; // 当前选中类目在窗口的索引
    // 点击类目当前下标大于5时，需要滚动到窗口中间;
    if (currentWinIndex > 5 && categoryWinRef.current) {
      categoryWinRef.current.scrollTo({
        left: (startIndex + currentWinIndex - 5.5) * (categoryItemWidth + 16),
        behavior: "smooth",
      });
    }
  };

  // 计算当前起点下标
  const handleComponentStartIndex = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (target) {
      const scrollLeft = target.scrollLeft;
      const categoryIndex = Math.ceil(scrollLeft / (categoryItemWidth + 16));
      setStartIndex(categoryIndex);
    }
  };

  // 防抖函数
  const debounceFn = <T extends any[]>(fn: (...args: T) => void, delay: number) => {
    let timer: number | null = null;
    return (...args: T) => {
      if (timer !== null) {
        clearTimeout(timer);
      }
      timer = window.setTimeout(() => fn(...args), delay);
    };
  };

  return (
    <div className={styles["second-category-container"]}>
      {categoryList.length > 12 && (
        <button onClick={handlePrevClick} disabled={startIndex === 0} className={styles["pre-btn"]}>
          <VectorLeft />
        </button>
      )}
      <div className={styles["category-window"]} ref={categoryWinRef} onScroll={debounceFn(handleComponentStartIndex, 500)}>
        <div className={styles["category-wrapper"]}>
          {categoryList.map((category, index) => (
            <div
              key={category.id}
              className={`
                ${styles["category-item"]} 
                ${index >= startIndex && index < startIndex + maxVisibleCategories ? styles["category-visible"] : ""}
                ${l3Clicked === category.id ? styles["category-selected"] : ""}
                `}
              style={{
                width: categoryItemWidth,
              }}
              onClick={() => handleSelectThirdCategory(category, index)}
            >
              <SafeLink
                href={buildSearchUrl(searchParams, { l3: String(category.id), k: category.cateName, page: "1" }, { basePath })}
                onClick={() => {
                  setL3Clicked(category.id);
                }}
                scroll={false}
              >
                <div className={styles["category-image"]}>
                  {category.cateImageUri ? (
                    <CDNImage
                      style={{backgroundColor:category?.backgroundColor}}
                      src={category.cateImageUri}
                      width={categoryItemWidth}
                      height={categoryItemWidth}
                      title={category.cateName}
                      alt={category.cateName}
                    />
                  ) : (
                    <div className={styles["image-empty"]}></div>
                  )}
                </div>
                <div className={styles["category-name"]}>{category.cateName}</div>
              </SafeLink>
            </div>
          ))}
        </div>
      </div>
      {categoryList.length > 12 && (
        <button onClick={handleNextClick} disabled={startIndex >= categoryList.length - maxVisibleCategories} className={styles["next-btn"]}>
          <VectorRight />
        </button>
      )}
    </div>
  );
};

export default ThirdLevelCate;
